import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  getUrlParameter,
  navigateToUrlByCusip,
} from '../../../../global/data/url.js'
import {
  isLoggedIntoSubscriptionCenter,
  isInvestor,
} from '../../../../global/authentication/authentication-manager.js'
import { init } from '../../../../global/authentication/secure-link-manager.js'
import { MODAL_TYPE_FUND } from '../../../EmailSubscriptionCenter/Landing/Helper/constants.js'
import {
  FUTURE_SCHOLAR_529,
  VARIABLE_FUND,
  CLOSED_END_FUND,
  MODEL_PORTFOLIOS,
} from '../../Helpers/FundTypes.js'
import {
  getFundHeaderTitle,
  getOpenMessage,
} from './Helpers/FundHeaderUtility.js'
import { formatDateStandard } from '../../../../global/formatters/string-formatter.js'

export default function FundHeaderMain({
  truncate,
  fund,
  isEtf,
  isGovtMoneyMarketFund,
  setShowEmailSubscriptionCenterModal,
  fundSubscriptionDocs,
  mergerDate,
  productList,
}) {
  const [truncatedHeader, setTruncatedHeader] = useState(truncate)
  const [showOtherFunds, setShowOtherFunds] = useState(false)

  const { fundHeader } = fund
  const renderDropdown = fundHeader?.classes?.value?.length > 0

  useEffect(() => {
    init()
  }, [])

  const updateStateOnStickyHeader = () => {
    window.addEventListener(
      'stickyHeaderActive',
      () => {
        if (!truncatedHeader) {
          setTruncatedHeader(true)
        }
      },
      false
    )

    window.addEventListener(
      'stickyHeaderInactive',
      () => {
        if (truncatedHeader && !showOtherFunds) {
          setTruncatedHeader(false)
        }
      },
      false
    )

    window.addEventListener(
      'hideMfViewer',
      () => {
        const button = document.getElementById('toggle-mf-viewer')

        const fundViewer = document.getElementById('mutual-fund-viewer')

        button.classList.remove('open')
        fundViewer.classList.add('u-hidden')
        fundViewer.setAttribute('aria-hidden', true)

        setTruncatedHeader(true)
        setShowOtherFunds(false)
      },
      false
    )

    const fundViewerClose = document.getElementById('mutual-fund-viewer-close')
    if (fundViewerClose) {
      const btn = document.getElementById('toggle-mf-viewer')
      fundViewerClose.addEventListener('click', () => {
        toggleFundViewer({ currentTarget: btn })
      })
      document.body.addEventListener('keydown', function (e) {
        if (e.key == 'Escape') {
          toggleFundViewer({ currentTarget: btn })
        }
      })
    }
  }

  const toggleFundViewer = (event) => {
    const fundViewer = document.getElementById('mutual-fund-viewer')
    const button = event.currentTarget

    if (truncatedHeader) {
      window.scrollTo(0, 0)
    }

    if (!showOtherFunds) {
      button.classList.add('open')
      fundViewer.classList.remove('u-hidden')
      fundViewer.setAttribute('aria-hidden', false)

      setTruncatedHeader(true)
      setShowOtherFunds(true)
    } else {
      button.classList.remove('open')
      fundViewer.classList.add('u-hidden')
      fundViewer.setAttribute('aria-hidden', true)

      setTruncatedHeader(truncate) // set it back to original state
      setShowOtherFunds(false)
    }
  }

  const handleClassChangeClick = (event) => {
    const select = event.currentTarget
    const option = select.options[select.selectedIndex]
    navigateToUrlByCusip(option.value, option.text)
  }

  const hasSubscribedSubscriptions = () => {
    return fundSubscriptionDocs.find((doc) => doc.selected)
  }

  const renderFundHeaderTitle = (fund) => {
    return (
      fund && (
        <>
          <button
            id="toggle-mf-viewer-mobile"
            onClick={toggleFundViewer}
            className="Link Link--trigger u-textSecondary u-md-hidden u-lg-hidden u-cursorPointer"
          >
            {getOpenMessage(showOtherFunds, fund.fundGroup)}
          </button>
          {getFundHeaderTitle(fund)}
        </>
      )
    )
  }

  const renderClassDropdown = ({ classes }) => {
    const cusip = getUrlParameter('cusip')

    return (
      <select
        className="Dropdown-menu fund-className-dropdown"
        defaultValue={cusip}
        onChange={handleClassChangeClick}
      >
        {classes.value.map((className, i) => {
          return (
            <option key={className.cusip + i} value={className.cusip}>
              {className.name}
            </option>
          )
        })}
      </select>
    )
  }

  const renderOverallMorningStar = (
    { mStarOverallRating, mStarRatingAsOfDt },
    mergerDate
  ) => {
    if (mStarOverallRating) {
      let stars = []

      for (let i = 0; i < mStarOverallRating; i++) {
        stars.push(
          <i key={i} aria-hidden="true" role="presentation">
            &#x2605;
          </i>
        )
      }

      return (
        <li className="u-marginRight6gu u-marginTop5gu u-sizeFit" key={stars}>
          <p className="u-marginBottom1gu">Overall Morningstar Rating</p>
          <span className="Icon u-textColorBrandLight u-textDefault u-textSizeMsrp3">
            {!mergerDate ? stars : '-'}
          </span>
          {!mergerDate && (
            <p className="u-textSizeMsrm2">
              as of {formatDateStandard(mStarRatingAsOfDt)}
            </p>
          )}
        </li>
      )
    }
  }

  const renderMorningStarOverallRisk = ({ mStarRiskVsCtg }, mergerDate) => {
    return (
      <li className="u-marginRight6gu u-marginTop5gu u-sizeFit">
        <p>Morningstar Overall Risk</p>
        {!mergerDate ? (
          <p className="u-marginTop1gu u-textSizeMsrp2 u-textSecondary">
            {mStarRiskVsCtg}
            <span className="Icon Icon--cti u-marginLeft1gu u-textColorWhite u-textSizeMsrp1">
              {compareRiskGaugeWithMorningStarRisk(mStarRiskVsCtg)}
            </span>
          </p>
        ) : (
          <p className="u-marginTop1gu u-textSizeMsrp2 u-textSecondary">-</p>
        )}
      </li>
    )
  }

  const compareRiskGaugeWithMorningStarRisk = (mStarRisk) => {
    const riskGauge = ['Low', 'Below Avg.', 'Average', 'Above Avg.', 'High']

    return riskGauge.map((risk) => {
      if (mStarRisk === risk) {
        return (
          <i
            aria-hidden="true"
            className="u-textColorBrandLight"
            role="presentation"
            key={risk}
            value={risk}
          >
            &#x25ac;
          </i>
        )
      } else {
        return (
          <i aria-hidden="true" role="presentation" key={risk} value={risk}>
            &#x25ac;
          </i>
        )
      }
    })
  }

  const renderAssetClass = ({ investCatg }, mergerDate) => {
    return (
      <>
        <li
          className="u-marginRight6gu u-marginTop5gu u-sizeFit"
          data-collapsible="true"
        >
          <p>
            {fund.fundGroup === MODEL_PORTFOLIOS
              ? 'Implementation Style'
              : 'Asset Class'}
          </p>
          {!isEtf &&
            (!mergerDate ? (
              <div className="Grid Grid--alignMiddle Grid--fit u-paddingVert1gu">
                <p className="u-textColorWhite u-textSizeMsrp2 u-textSecondary">
                  {investCatg}
                </p>
              </div>
            ) : (
              <p className="u-textColorWhite u-textSizeMsrp2 u-textSecondary u-marginTop1gu">
                -
              </p>
            ))}
          {isEtf && (
            <p className="u-textColorWhite u-textSizeMsrp2 u-textSecondary u-marginTop1gu">
              {!mergerDate ? investCatg : '-'}
            </p>
          )}
        </li>
      </>
    )
  }

  const renderFactSheet = (fund, truncatedHeader) => {
    const header = fund.fundHeader

    return (
      header.fundFactsURL && (
        <a
          href={header.fundFactsURL}
          target="_blank"
          rel="noopener noreferrer"
          data-analytics="Fact Sheet"
          className={`Button Button--secondary Button--reversed Button--small u-sm-hidden u-textNoTransform ${
            truncatedHeader
              ? 'u-marginRight2gu'
              : 'Button--block u-marginBottom2gu'
          }`}
        >
          Fact Sheet (PDF)
        </a>
      )
    )
  }

  const renderSubscriptionCenterButtons = () => {
    const showSubscriptionModal = () => {
      setShowEmailSubscriptionCenterModal(true, MODAL_TYPE_FUND)
    }

    return (
      fundSubscriptionDocs?.length > 0 && (
        <>
          {isLoggedIntoSubscriptionCenter() && hasSubscribedSubscriptions() ? (
            <button
              onClick={showSubscriptionModal}
              className="Button Button--block u-textColorWhite u-sm-marginTop4gu u-textUppercase u-padding0 u-marginBottom2gu u-textColorBrandDark"
              data-analytics="cesc-subscribe-link"
              data-qa-automation="subscribe-button"
            >
              <span className="Icon--material u-textColorWhite u-textSizeMsrp2 u-marginRight1gu u-posRelative">
                mail_outline
                <span className="Icon--material u-posAbsolute u-textColorWhite u-bgColorConfirm u-textSizeMsrm2 u-textBold Subscribed-icon">
                  check
                </span>
              </span>
              Manage Subscriptions
            </button>
          ) : (
            <button
              onClick={showSubscriptionModal}
              className="Button Button--primary Button--reversed Button--block Button--small u-marginBottom2gu u-sm-marginTop4gu u-textColorBrandDark"
              data-analytics="cesc-subscribe-link"
              data-qa-automation="subscribe-button"
            >
              <span className="adel-material-symbols u-marginRight1gu">
                mail_outline
              </span>
              Subscribe{' '}
            </button>
          )}
        </>
      )
    )
  }

  if (!mergerDate) {
    updateStateOnStickyHeader()
  }

  return (
    <div
      className="u-bgColorBrandDark"
      data-analytics-component-name="Fund Details Banner"
    >
      <div className="js-fund-header-expanded u-sizeConstrained">
        <div
          className="Grid u-paddingVert4gu u-sizeFull u-textReversed"
          id="js-fund-header-grid"
        >
          <div
            className="u-md-sizeFill u-lg-sizeFill"
            id="js-fundHeader-titleSection"
          >
            <div
              className="u-marginBottom4gu u-sizeFill"
              id="js-fundHeader-titleWrapper"
            >
              {renderFundHeaderTitle(fund)}
            </div>

            {renderDropdown && (
              <ul className="u-flex u-flexAlignItemsCenter u-textReversed">
                <li
                  className="u-marginRight3gu"
                  id="js-fundHeader-classNameDropdown"
                >
                  <label
                    className="Dropdown-label Dropdown-label--reversed u-inlineBlock u-paddingRight2gu u-textNormal"
                    data-collapsible="true"
                    htmlFor="dropdown-menu"
                  >
                    Class
                  </label>
                  <div className="Dropdown Dropdown--reversed fund-className-dropdown u-inlineBlock">
                    {renderClassDropdown(fundHeader)}
                  </div>
                </li>
              </ul>
            )}

            <ul
              className={`Grid u-textReversed u-textSecondary ${
                truncatedHeader ? 'u-hidden' : ''
              }`}
              data-collapsible="true"
            >
              {fund.fundGroup !== FUTURE_SCHOLAR_529 &&
                fund.fundGroup !== VARIABLE_FUND &&
                fund.fundGroup !== CLOSED_END_FUND &&
                fund.fundGroup !== MODEL_PORTFOLIOS &&
                fundHeader &&
                fundHeader.mStarOverallRating &&
                fundHeader.mStarOverallRating >= 4 &&
                renderOverallMorningStar(fundHeader, mergerDate)}

              {fund.fundGroup !== FUTURE_SCHOLAR_529 &&
                fund.fundGroup !== VARIABLE_FUND &&
                fund.fundGroup !== CLOSED_END_FUND &&
                fund.fundGroup !== MODEL_PORTFOLIOS &&
                fundHeader &&
                fundHeader.mStarCategory && (
                  <li className="u-marginRight6gu u-marginTop5gu u-sizeFit">
                    <p>Morningstar Category</p>
                    <p className="u-marginTop1gu u-textSizeMsrp2 u-textSecondary">
                      {!mergerDate ? fundHeader.mStarCategory : '-'}
                    </p>
                  </li>
                )}

              {fund.fundGroup !== FUTURE_SCHOLAR_529 &&
                fund.fundGroup !== VARIABLE_FUND &&
                fund.fundGroup !== CLOSED_END_FUND &&
                fund.fundGroup !== MODEL_PORTFOLIOS &&
                fundHeader &&
                fundHeader.mStarRiskVsCtg &&
                renderMorningStarOverallRisk(fundHeader, mergerDate)}

              {fund.fundGroup !== FUTURE_SCHOLAR_529 &&
                fund.fundGroup !== VARIABLE_FUND &&
                fund.fundGroup !== CLOSED_END_FUND &&
                fundHeader &&
                fundHeader.investCatg &&
                renderAssetClass(fundHeader, mergerDate)}

              {fund.fundGroup !== VARIABLE_FUND &&
                fund.fundGroup !== CLOSED_END_FUND &&
                fund.fundGroup !== MODEL_PORTFOLIOS &&
                fundHeader &&
                fundHeader.subCtgValue && (
                  <li className="u-marginRight6gu u-marginTop5gu u-sizeFit">
                    <p>Strategy</p>
                    <p className="u-marginTop1gu u-textSizeMsrp2 u-textSecondary">
                      {!mergerDate ? fundHeader.subCtgValue : '-'}
                    </p>
                  </li>
                )}
              {isEtf && fund?.managementStyle && (
                <li className="u-marginRight6gu u-marginTop5gu u-sizeFit">
                  <p>Management Style</p>
                  <p className="u-marginTop1gu u-textSizeMsrp2 u-textSecondary">
                    {fund.managementStyle}
                  </p>
                </li>
              )}
            </ul>
          </div>

          <div
            className="u-paddingLeft4gu u-sizeFit u-sm-paddingLeft0 u-sm-sizeFull"
            id="fundHeader-linkSection"
          >
            <ul id="js-fundHeader-links">
              {fund.fundGroup != 'Interval Fund' &&
                productList &&
                ((productList.value && productList.value.products) ||
                  productList.productList) && (
                  <li
                    className={`u-marginBottom4gu u-paddingBottom1gu u-paddingTop1gu u-sm-hidden ${
                      truncatedHeader ? 'u-textRight' : 'u-textCenter'
                    }`}
                    id="js-fundHeader-textLink"
                  >
                    <button
                      id="toggle-mf-viewer"
                      onClick={toggleFundViewer}
                      className="Link--reversed u-textDecoUnderline Link--trigger u-textSecondary u-cursorPointer"
                    >
                      {getOpenMessage(showOtherFunds, fund.fundGroup)}
                    </button>
                  </li>
                )}
              {!mergerDate && renderSubscriptionCenterButtons()}

              {!mergerDate && (
                <li
                  className="u-marginBottom2gu"
                  id="js-fundHeader-buttonLinks"
                >
                  {!isGovtMoneyMarketFund &&
                    renderFactSheet(
                      fund,
                      truncatedHeader && fundHeader?.prospectusURL
                    )}
                  {fundHeader && fundHeader.prospectusURL && (
                    <a
                      href={fundHeader.prospectusURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-analytics="Prospectus"
                      className={`Button Button--secondary Button--reversed Button--small u-sm-hidden u-textNoTransform ${
                        truncatedHeader ? '' : 'Button--block u-marginBottom2gu'
                      }`}
                    >
                      Prospectus (PDF)
                    </a>
                  )}
                </li>
              )}

              {!mergerDate &&
                !isInvestor() &&
                fundHeader &&
                fundHeader.attrReportURL &&
                !truncatedHeader && (
                  <li className="u-marginBottom2gu" data-collapsible="true">
                    <a
                      href={fundHeader.attrReportURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-analytics="Attribution/Contribution Report"
                      className="Link--reversed locked-document-link locked-content-icon-link u-textColorWhite u-paddingVert0 u-block u-textSecondary u-sm-hidden"
                    >
                      <span className="u-textDecoUnderline">
                        Attribution/Contribution (PDF)
                      </span>
                    </a>
                  </li>
                )}

              {!mergerDate &&
                fundHeader &&
                fundHeader.fundPresentURL &&
                !truncatedHeader && (
                  <li data-collapsible="true">
                    <a
                      href={fundHeader.fundPresentURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-analytics="Fund Presentation"
                      className="Button Button--secondary Button--reversed Button--block u-marginBottom2gu Button--small u-textNoTransform"
                    >
                      Fund Presentation (PDF)
                    </a>
                  </li>
                )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

FundHeaderMain.propTypes = {
  fund: PropTypes.object.isRequired,
  truncate: PropTypes.bool.isRequired,
  showOtherFunds: PropTypes.bool,
  setShowEmailSubscriptionCenterModal: PropTypes.func,
  fundSubscriptionDocs: PropTypes.array,
}
